import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import {
  ScopeElementsConfig,
  UpdateDeliverablePayload,
  UpdateFeeItemPayload,
  UpdateRolePayload,
  UpdateTpcPayload,
} from '../models'

import { CdnConfig } from '@app/core/model/cdn-config.model'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { trackById } from '@app/shared/utils/utils'
import { ScopeElementsService } from '../services/scope-elements.service'

import { ChangeItem, ChangeType } from '@app/features/scope-overview/change-request/model'
import { UpdateComponentPayload } from '../models/update-component-payload.interface'

import { Store } from '@ngrx/store'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { Observable } from 'rxjs'
import { DeliverableWithChanges } from '../models/entities-with-changes/deliverable-with-changes.interface'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { Dictionary } from 'lodash'

@Component({
  selector: 'scope-deliverables',
  templateUrl: './deliverables.component.html',
  styleUrls: ['./deliverables.component.scss'],
})
export class DeliverablesComponent implements OnChanges {
  @Input() level = 2
  @Input() config: ScopeElementsConfig
  @Input() cdnConfig?: CdnConfig
  @Input() deliverables: DeliverableWithChanges[] = []
  @Input() section?: ScopeSection
  @Input() changeItemsMap: Dictionary<ChangeItem>

  @Input() deliverableMenuOptions: MenuOptions[]
  @Input() componentMenuOptions: MenuOptions[]
  @Input() feeMenuOptions: MenuOptions[]

  @Output() onExpandDeliverable = new EventEmitter<{ scopeId: number; deliverableId: number }>()
  @Output() onUpdateDeliverable = new EventEmitter<UpdateDeliverablePayload>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()
  @Output() onDropDeliverable = new EventEmitter<{
    event: CdkDragDrop<DeliverableWithChanges[]>
    section: ScopeSection | null
  }>()

  @Output() onUpdateComponent = new EventEmitter<UpdateComponentPayload>()
  @Output() onOverrideComponentQuantity = new EventEmitter<ScopeComponent>()
  @Output() onOverrideRateCardRoleHours = new EventEmitter<UpdateRolePayload>()
  @Output() onUpdateRole = new EventEmitter<UpdateRolePayload>()
  @Output() onMoveComponent = new EventEmitter<{ component: ScopeComponent; deliverable: Deliverable }>()
  @Output() onMoveComponentOutsideDeliverable = new EventEmitter<ScopeComponent>()
  @Output() onUpdateFeeItem = new EventEmitter<UpdateFeeItemPayload>()
  @Output() onUpdateDeliverableTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateComponentTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onUpdateDeliverableSectionTpc = new EventEmitter<UpdateTpcPayload>()
  @Output() onScrolledDown = new EventEmitter()
  @Output() onReorderDeliverableTpcs = new EventEmitter<{
    deliverable: Deliverable
    thirdPartyCosts: ThirdPartyCost[]
  }>()
  @Output() onReorderComponentTpcs = new EventEmitter<any>()
  @Output() onReorderDeliverableSectionTpcs = new EventEmitter<any>()

  deliverableLoadingMap$ = (sectionId: number, id: number): Observable<boolean> =>
    this.store$.select(ScopeOverviewSelectors.selectDeliverableLoading(sectionId, id))

  constructor(
    private store$: Store,
    protected scopeTabService: ScopeTabService,
    protected scopeElementsService: ScopeElementsService
  ) {
    scopeElementsService.setFeeColumns()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.scopeVersion && this.config.scopeVersion && !this.scopeElementsService.userColumns)
      this.scopeElementsService.setUserColumns(this.config.scopeVersion)
  }

  expandDeliverable(scopeId: number, deliverableId: number) {
    this.onExpandDeliverable.emit({ scopeId, deliverableId })
  }

  protected readonly trackById = trackById
  protected readonly ChangeType = ChangeType
}
